import fetchEndpoint from '@/services/fetchEndpoint';
import type { ScoreBreakdownData } from '@/components/ScoreBreakdown/ScoreBreakdown';

export type TimelineItemDto = {
  pmYear: number;
  citationCount: number;
};

export type EvidenceTimelineFetchResult = {
  timeline: TimelineItemDto[] | null;
  publicationsByYear?: Record<number, unknown> | null;
  globalScore?: ScoreBreakdownData | null;
};

function pickScalarField(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (Array.isArray(v)) return pickScalarField(v[0]);
  const s = String(v).trim();
  return s || undefined;
}

/** Disease + gene (GDA) or variant (VDA) for evidences-with-timeline requests. */
export function getRowAssociationTimelineParams(
  row: Record<string, unknown> | null | undefined,
  tab: string,
): {
  diseaseFilter: string;
  geneSymbol?: string;
  variantName?: string;
  apiTab: string;
} | null {
  if (!row) return null;
  const t = String(tab || '').toUpperCase();
  const diseaseRaw = pickScalarField(row.diseaseUMLSCUI);
  if (!diseaseRaw) return null;
  const diseaseFilter = diseaseRaw.replace(/^UMLS_/, '');
  if (!diseaseFilter) return null;

  if (t === 'GDA') {
    const geneSymbol = pickScalarField(row.geneSymbol);
    if (!geneSymbol) return null;
    return { diseaseFilter, geneSymbol, apiTab: 'gda' };
  }
  if (t === 'VDA') {
    const variantName = pickScalarField(row.variantStrID ?? row.strID);
    if (!variantName) return null;
    return { diseaseFilter, variantName, apiTab: 'vda' };
  }
  return null;
}

export function extractGlobalScoreFromEvidencesPayload(payload: unknown): ScoreBreakdownData | null {
  if (payload == null || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;

  const gs = p.globalScore;
  if (gs && typeof gs === 'object') {
    const g = gs as Record<string, unknown>;
    const total = Number(g.total);
    const componentsRaw = g.components;
    if (!Number.isFinite(total) || componentsRaw == null || typeof componentsRaw !== 'object') return null;
    const components = { ...(componentsRaw as Record<string, unknown>) };
    for (const key of Object.keys(components)) {
      const block = components[key];
      if (
        block &&
        typeof block === 'object' &&
        'sources' in block &&
        (block as Record<string, unknown>).sources == null
      ) {
        (block as Record<string, unknown>).sources = [];
      }
    }
    return {
      total,
      components: components as ScoreBreakdownData['components'],
    };
  }

  if (p.payload && typeof p.payload === 'object') {
    return extractGlobalScoreFromEvidencesPayload(p.payload);
  }

  return null;
}

function normalizeTimelineItem(it: unknown): TimelineItemDto | null {
  if (!it || typeof it !== 'object') return null;
  const o = it as Record<string, unknown>;
  const pmYear = Number(o.pmYear ?? o.year);
  const citationCount = Number(o.citationCount ?? o.evidence ?? o.citations);
  if (!Number.isFinite(pmYear) || !Number.isFinite(citationCount)) return null;
  return { pmYear, citationCount };
}

function normalizeTimelineArray(raw: unknown): TimelineItemDto[] {
  if (!Array.isArray(raw)) return [];
  const out: TimelineItemDto[] = [];
  for (const it of raw) {
    const n = normalizeTimelineItem(it);
    if (n) out.push(n);
  }
  return out;
}

export function extractTimelineFromEvidencesPayload(payload: unknown): EvidenceTimelineFetchResult {
  if (payload == null) return { timeline: null };
  if (Array.isArray(payload)) {
    const t = normalizeTimelineArray(payload);
    return { timeline: t.length ? t : null };
  }
  if (typeof payload !== 'object') return { timeline: null };
  const p = payload as Record<string, unknown>;

  if (Array.isArray(p.timeline)) {
    const timeline = normalizeTimelineArray(p.timeline);
    return {
      timeline: timeline.length ? timeline : null,
      publicationsByYear: (p.publicationsByYear as Record<number, unknown>) ?? null,
    };
  }

  if (p.payload && typeof p.payload === 'object') {
    return extractTimelineFromEvidencesPayload(p.payload);
  }

  if (Array.isArray(p.data)) {
    const timeline = normalizeTimelineArray(p.data);
    return { timeline: timeline.length ? timeline : null };
  }

  return { timeline: null };
}

export function buildGdaEvidencesWithTimelineQuery(params: {
  source: string;
  diseaseFilter: string;
  page_number?: number;
  page_size?: number;
  tab?: string;
  geneSymbol?: string;
  variantName?: string;
  minPmidYear?: number | null;
  maxPmidYear?: number | null;
}): string {
  const tab = (params.tab ?? 'gda').toLowerCase();
  const diseaseFilter = String(params.diseaseFilter ?? '').replace(/^UMLS_/, '');
  const search = new URLSearchParams({
    source: params.source,
    page_number: String(params.page_number ?? 0),
    page_size: String(params.page_size ?? 50),
    disease_filter: diseaseFilter,
  });
  if (tab === 'vda') {
    if (params.variantName) search.set('variant_name', params.variantName);
  } else if (params.geneSymbol) {
    search.set('gene_symbol', params.geneSymbol);
  }
  const minY = params.minPmidYear;
  const maxY = params.maxPmidYear;
  if (minY != null && Number.isFinite(minY)) search.set('min_pmid_year', String(Math.trunc(minY)));
  if (maxY != null && Number.isFinite(maxY)) search.set('max_pmid_year', String(Math.trunc(maxY)));
  return `api/${tab}/evidences-with-timeline?${search.toString()}`;
}

export async function fetchGdaEvidencesWithTimeline(params: {
  source: string;
  diseaseFilter: string;
  page_number?: number;
  page_size?: number;
  authorization: unknown;
  setAuthError: (e: unknown) => void;
  tab?: string;
  geneSymbol?: string;
  variantName?: string;
  minPmidYear?: number | null;
  maxPmidYear?: number | null;
}): Promise<EvidenceTimelineFetchResult> {
  const endpoint = buildGdaEvidencesWithTimelineQuery(params);
  const res = (await fetchEndpoint(endpoint, params.authorization, params.setAuthError)) as {
    status?: string;
    payload?: unknown;
  };
  if (res?.status !== 'OK') {
    return { timeline: null, globalScore: null };
  }
  const base = extractTimelineFromEvidencesPayload(res.payload);
  const globalScore = extractGlobalScoreFromEvidencesPayload(res.payload);
  return { ...base, globalScore: globalScore ?? null };
}
